import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { getWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook } from "../api.js";
import type { Webhook } from "@tuit/shared-types";
import styles from "../styles/pages/SettingsPage.module.css";

export function WebhooksPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { status: billing } = useBillingStatus();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    getWebhooks().then(setWebhooks).catch(() => {});
  }, [isSignedIn]);

  if (!isLoaded || !isSignedIn) return null;

  const canWebhooks = billing?.capabilities.canWebhooks ?? false;

  function flash(text: string) { setMsg(text); setTimeout(() => setMsg(null), 3500); }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const wh = await createWebhook({ url: url.trim() });
      setWebhooks((prev) => [...prev, wh]);
      setUrl("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add webhook");
    } finally {
      setAdding(false);
    }
  }

  async function handleToggle(wh: Webhook) {
    setBusyId(wh.id);
    try {
      const updated = await updateWebhook(wh.id, { enabled: !wh.enabled });
      setWebhooks((prev) => prev.map((w) => (w.id === wh.id ? updated : w)));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id: string) {
    setBusyId(id);
    try {
      await deleteWebhook(id);
      setWebhooks((prev) => prev.filter((w) => w.id !== id));
    } finally {
      setBusyId(null);
    }
  }

  async function handleTest(id: string) {
    setBusyId(`test_${id}`);
    try {
      await testWebhook(id);
      flash("Test delivery sent");
    } catch {
      flash("Test failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
      </nav>
      <div className={styles.inner}>
        <h1 className={styles.heading}>Webhooks</h1>
        <p className={styles.sub}>Push new posts to external URLs as they arrive in your feeds.</p>

        {!canWebhooks ? (
          <div className={styles.upsell}>
            <p>Webhooks are available on the <strong>Pro</strong> plan.</p>
            <Link to="/premium" className={styles.upsellLink}>Upgrade now →</Link>
          </div>
        ) : (
          <>
            <form className={styles.card} onSubmit={handleAdd}>
              <div className={styles.inlineFields}>
                <input
                  type="url"
                  className={styles.input}
                  placeholder="https://your-endpoint.com/hook"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <button type="submit" className={styles.primaryBtn} disabled={adding || !url.trim()}>
                  {adding ? "Adding…" : "Add"}
                </button>
              </div>
              {error && <p className={styles.error}>{error}</p>}
            </form>

            {webhooks.length > 0 && (
              <ul className={styles.list}>
                {webhooks.map((wh) => (
                  <li key={wh.id} className={styles.listItem}>
                    <span className={`${styles.dot} ${wh.enabled ? styles.dotGreen : styles.dotGray}`} />
                    <span className={styles.listItemText}>{wh.url}</span>
                    <div className={styles.rowActions}>
                      <button type="button" className={styles.smallBtn} onClick={() => handleTest(wh.id)} disabled={busyId === `test_${wh.id}`}>Test</button>
                      <button type="button" className={styles.smallBtn} onClick={() => handleToggle(wh)} disabled={busyId === wh.id}>
                        {wh.enabled ? "Disable" : "Enable"}
                      </button>
                      <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(wh.id)} disabled={busyId === wh.id}>Remove</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {webhooks.length === 0 && <p className={styles.hint}>No webhooks yet.</p>}
          </>
        )}

        {msg && <p className={styles.flash}>{msg}</p>}
      </div>
    </div>
  );
}
